/** Field-GPS expectation toggle.
 *
 * Backlog 2026-09-21 — this used to hard-block a field user from joining
 * at all without a working GPS fix. It no longer blocks anything, for
 * either value: the /join consent gate always lets the call through,
 * showing a red banner + proceeding with geo=null on a failed fix, and
 * the report separately flags "GPS not captured". This toggle is now
 * purely a stated expectation ("we require field agents to share
 * location") — org admins (perm: org.update) still set it here, but it
 * doesn't gate the join flow.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { describeApiError, isMfaRequired } from "@/lib/apiErrors";
import type { Organization } from "@/lib/types";


export function FieldGeoRequiredCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await api.patch<Organization>(ApiPaths.orgGeoRequired, {
        require_field_geo: next,
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-me"], next);
    },
  });

  if (org.isLoading) return null;
  if (!org.data) return null;

  const enabled = org.data.require_field_geo;

  return (
    <section className="card space-y-2">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.fieldGeo.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.fieldGeo.cardHint")}</p>
      </header>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-sm text-nb-text">
          <span className="text-nb-text-2">
            {t("admin.fieldGeo.statusLabel")}
          </span>{" "}
          <span
            className={
              enabled
                ? "font-medium text-cvp-ok"
                : "font-medium text-nb-text-2"
            }
          >
            {enabled
              ? t("admin.fieldGeo.statusEnabled")
              : t("admin.fieldGeo.statusDisabled")}
          </span>
        </span>
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => toggle.mutate(!enabled)}
          disabled={toggle.isPending}
        >
          {toggle.isPending
            ? t("admin.fieldGeo.saving")
            : enabled
              ? t("admin.fieldGeo.disableButton")
              : t("admin.fieldGeo.enableButton")}
        </button>
      </div>
      {!enabled && (
        <p className="text-xs text-cvp-warn">
          {t("admin.fieldGeo.disabledWarning")}
        </p>
      )}
      {toggle.isError && (
        <p className="text-xs text-cvp-danger">
          {isMfaRequired(toggle.error)
            ? t("admin.mfaRequiredHint")
            : describeApiError(toggle.error, t("admin.fieldGeo.saveErrorGeneric"))}
        </p>
      )}
    </section>
  );
}
